import { QueryResult, useQuery } from "@apollo/client";
import {
  GetReportDetailsQuery,
  GetReportDetailsQueryVariables,
  ProgramArea,
} from "generated-graphql/graphql";
import React, { createContext } from "react";

import { useSnapshotPendingPolling } from "../../../../components/snapshotState";
import { GET_REPORT } from "./useReport";

export const ReportContext = createContext<
  QueryResult<GetReportDetailsQuery, GetReportDetailsQueryVariables> | undefined
>(undefined);

/**
 * Surfaces whether snapshot polling has hit its hard timeout so the report
 * page can swap the "refreshing automatically" spinner for a static
 * check-back message instead of spinning forever.
 */
export const SnapshotPollingContext = createContext<{
  pollingTimedOut: boolean;
}>({
  pollingTimedOut: false,
});

export function ReportProvider({
  reportId,
  children,
}: {
  reportId: string | undefined;
  children: React.ReactNode;
}) {
  const queryResult = useQuery(GET_REPORT, {
    variables: {
      reportId: reportId ?? "",
      programArea: ProgramArea.Epcra,
      version: undefined,
    },
    skip: !reportId,
    fetchPolicy: "network-only",
  });

  // Auto-poll (with a hard timeout) while the snapshot is being created so the
  // report appears as soon as the async job finishes.
  const { pollingTimedOut } = useSnapshotPendingPolling(queryResult);

  return (
    <ReportContext.Provider value={queryResult}>
      <SnapshotPollingContext.Provider value={{ pollingTimedOut }}>
        {children}
      </SnapshotPollingContext.Provider>
    </ReportContext.Provider>
  );
}
